import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Linkedin, MapPin, Send, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! I'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 hero-gradient grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl">

            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-royal-blue-800 mb-4 block">
              Contact
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-zinc-950 mb-6">
              Let's Start a Conversation
            </h1>
            <p className="text-lg text-zinc-600 leading-relaxed">
              Whether you have a question about AI strategy, want to discuss a project, 
              or just want to connect — I'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}>

              <h2 className="font-display text-2xl font-semibold text-zinc-950 mb-6">
                Send a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form" id="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-zinc-50 border-zinc-200"
                      data-testid="contact-name-input" />

                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-zinc-50 border-zinc-200"
                      data-testid="contact-email-input" />

                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    name="subject"
                    placeholder="What would you like to discuss?"
                    value={formData.subject}
                    onChange={handleChange}
                    className="bg-zinc-50 border-zinc-200"
                    data-testid="contact-subject-input" />

                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell me more about your project or question..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="bg-zinc-50 border-zinc-200 resize-none"
                    data-testid="contact-message-input" />

                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-royal-blue-800 hover:bg-royal-blue-700 text-white px-8 py-6 text-xs tracking-wider uppercase"
                  data-testid="contact-submit-btn">

                  {isSubmitting ?
                  "Sending..." :

                  <>
                      Send Message
                      <Send className="ml-2" size={16} />
                    </>
                  }
                </Button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}>

              <h2 className="font-display text-2xl font-semibold text-zinc-950 mb-6">
                Other Ways to Connect
              </h2>
              
              <div className="space-y-6 mb-12">
                <a
                  href="mailto:contact@vasanthnatarajan.me"
                  className="flex items-start gap-4 p-6 bg-zinc-50 border border-zinc-200 rounded-sm hover:border-royal-blue-800 transition-colors"
                  data-testid="contact-email-link">

                  <div className="skill-icon flex-shrink-0">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-950 mb-1">Email</h3>
                    <p className="text-royal-blue-800">contact@vasanthnatarajan.me</p>
                    <p className="text-sm text-zinc-500 mt-1">Click to open your email client</p>
                  </div>
                </a>

                <a
                  href="https://www.linkedin.com/in/vasan-natarajan"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-6 bg-zinc-50 border border-zinc-200 rounded-sm hover:border-royal-blue-800 transition-colors"
                  data-testid="contact-linkedin-link">

                  <div className="skill-icon flex-shrink-0">
                    <Linkedin size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-950 mb-1">LinkedIn</h3>
                    <p className="text-royal-blue-800">www.linkedin.com/in/vasan-natarajan</p>
                    <p className="text-sm text-zinc-500 mt-1">Connect for updates and insights</p>
                  </div>
                </a>

                <div
                  className="flex items-start gap-4 p-6 bg-zinc-50 border border-zinc-200 rounded-sm"
                  data-testid="contact-location">

                  <div className="skill-icon flex-shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-950 mb-1">Location</h3>
                    <p className="text-zinc-600">United States</p>
                    <p className="text-sm text-zinc-500 mt-1">Available for global engagements</p>
                  </div>
                </div>
              </div>

              {/* Schedule CTA */}
              <div className="p-8 bg-zinc-950 text-white rounded-sm">
                <Calendar className="mb-4 opacity-80" size={32} />
                <h3 className="font-display text-xl font-semibold mb-3">
                  Let's Talk AI
                </h3>
                <p className="text-zinc-400 mb-6">
                  Schedule a 30-minute conversation to exchange ideas on AI.
                </p>
                <Link to="/book">
                  <Button
                    variant="outline"
                    className="border-white text-white hover:bg-white hover:text-zinc-950 px-6"
                    data-testid="schedule-call-btn"
                  >
                    Start a Conversation
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>);

}